import { LazyQueryHookOptions, QueryHookOptions } from '@apollo/client';

import {
  LocationGroupFilter,
  LocationGroupFragment,
  LocationGroupsQuery,
  LocationGroupsQueryVariables,
  useLocationGroupsLazyQuery,
  useLocationGroupsQuery,
} from '@occc/graphql';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';

import { useLazyQuery } from '../utils';

type Options = QueryHookOptions<LocationGroupsQuery, LocationGroupsQueryVariables>;

interface GetLocationGroupsProps {
  filter?: LocationGroupFilter;
  pageSize?: number;
  baseOptions?: Options;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
}

const transformLocationGroupsQueryResponseToArray = (
  data?: LocationGroupsQuery,
): LocationGroupFragment[] => data?.locationGroups?.edges?.map((item) => item.node) || [];

export function useGetLocationGroups({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetLocationGroupsProps = {}) {
  const response = usePaginatedQuery(
    useLocationGroupsQuery,
    'locationGroups.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
    },
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: transformLocationGroupsQueryResponseToArray(response.data),
  };
}

export function useGetLocationGroup(uuid: string) {
  const response = useLocationGroupsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        uuidContains: [uuid],
      },
    },
  });

  return {
    ...response,
    data: response.data ? response.data?.locationGroups?.edges?.[0]?.node : response.data,
  };
}

export const useGetLocationGroupsByUuids = (uuids: string[], skip = false) => {
  const response = useLocationGroupsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: skip || uuids.length === 0,
    variables: {
      filter: {
        uuidContains: uuids,
      },
    },
  });

  return {
    ...response,
    data: response.data ? response.data?.locationGroups?.edges?.map((item) => item.node) : [],
  };
};

export const useGetLocationGroupsLazy = (
  baseOptions?: LazyQueryHookOptions<LocationGroupsQuery, LocationGroupsQueryVariables>,
) => {
  const [executeQuery, response] = useLazyQuery(useLocationGroupsLazyQuery, baseOptions);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data
        ? transformLocationGroupsQueryResponseToArray(response.data)
        : response.data,
    },
  };
};
